import { countRepos, getRepoTagCatalog, listRepos } from '@mesa/core/repo/queries';
import { createServerFn } from '@tanstack/react-start';
import { log } from '../../lib/logger';
import { tagCatalogCache } from '../../lib/repo-tag-catalog-cache';
import { assertScope, baseAuthMiddleware } from '../../middleware/base-auth';

export const loadRepoListPage = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator(
    (data: {
      orgSlug: string;
      tags?: string[];
      search?: string;
      cursor?: string;
      sortBy?: 'name' | 'createdAt';
      sortOrder?: 'asc' | 'desc';
    }) => data
  )
  .handler(async ({ data, context }) => {
    assertScope(context.member.role, 'read');
    const orgId = context.org.id;

    const [repoResult, totalCount] = await Promise.all([
      listRepos(orgId, {
        tags: data.tags?.length ? data.tags.join(',') : undefined,
        search: data.search || undefined,
        cursor: data.cursor,
        limit: 10,
        sortBy: data.sortBy,
        sortOrder: data.sortOrder,
      }),
      countRepos(orgId, {
        tags: data.tags?.length ? data.tags.join(',') : undefined,
        search: data.search || undefined,
      }),
    ]);

    return {
      repos: repoResult.repos,
      nextCursor: repoResult.nextCursor,
      hasMore: repoResult.hasMore,
      tagsByRepo: repoResult.tagsByRepo,
      totalCount,
    };
  });

// An org's label values can be unbounded (agents stamp repos with ID-valued
// labels), so the filter panel only ever shows the most common values per key.
// The full catalog once grew past Lambda's 6MB response cap and 502'd the
// repositories page for every viewer of that org.
const TAG_CATALOG_MAX_VALUES_PER_KEY = 50;

export const loadRepoTagCatalog = createServerFn({ method: 'GET' })
  .middleware([baseAuthMiddleware])
  .validator((data: { orgSlug: string }) => data)
  .handler(async ({ context }) => {
    assertScope(context.member.role, 'read');
    const orgId = context.org.id;
    const cached = tagCatalogCache.get(orgId);
    if (cached) return cached;
    try {
      const catalog = await getRepoTagCatalog(orgId, { maxValuesPerKey: TAG_CATALOG_MAX_VALUES_PER_KEY });
      tagCatalogCache.set(orgId, catalog);
      return catalog;
    } catch (err) {
      // Tags are cosmetic: an empty panel beats a broken one. Failures are
      // never cached, so one blip can't blank the panel for the whole TTL.
      log.warn('tag-catalog-failed', { orgId, error: String(err) });
      return {};
    }
  });
